/* ═══════════════════════════════════════════════════════════════════
 * ww-play-and-train.js
 * v1.0 — controller for the WUNDER Play & Train Pack PDP.
 *
 * Cloned from ww-starter-pack.js (2026-04-27). Adapted for 2 balls +
 * 2 treats (instead of 2 balls + 1 leash). Same locked patterns:
 * dynamic shipping line, Shop Pay intercept, MU sequential add, no
 * /discount/CODE redirect, per-line floor pricing math.
 *
 * 2026-04-27 — 2-ball + 2-treats architecture:
 *   • Bundle ships 2 WUNDERballs + 2 bags of WUNDERtreats at 15% off.
 *   • Two independent toggles: "Make them different" on balls, "Mix
 *     the flavors" on treats. Defaults: 1 picker each, qty 2.
 *   • ATC coalesces same-variant items into qty-2 lines, splits when
 *     differentiated. Cart can have 2, 3, or 4 line items depending
 *     on toggle states.
 *   • Bundle pricing: Shopify Automatic Discount targeting WUNDERball
 *     + WUNDERtreats with a 4-item minimum, Combinations: shipping
 *     only. See PLAY_AND_TRAIN_DEPLOY-2026-04-27.md.
 *   • Smallest combo lands BELOW $49 → dynamic shipping line shows
 *     "Add $X.XX more for free shipping." Customers can upsize to
 *     unlock free ship. Larger combos auto-clear.
 *
 * MONSTER UPSELLS CONTRACT (per HANDOFF §4.4 / §6.1):
 *   • Multi-item adds MUST be sequential.
 *   • Do NOT call /cart/add.js directly when MU is present.
 *   • Do NOT redirect to /discount/CODE — that's the OG bug.
 *
 * ════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  if (!window.PT_DATA) {
    console.warn('[PlayAndTrain] window.PT_DATA missing — section liquid did not inject data');
    return;
  }

  var DATA = window.PT_DATA;
  var SIZE_ORDER = ['Small', 'Medium', 'Large', 'XL'];

  /* ── State ── (initialized via initState() after we know variant values)
     ball   = primary ball (qty 2 by default, qty 1 when balls toggle on)
     ball2  = second ball (only used when balls toggle on; mirrors ball when off)
     treat  = primary treats (qty 2 by default, qty 1 when treats toggle on)
     treat2 = second treats (only used when treats toggle on)
     mixBalls  = "make them different" toggle state for balls
     mixTreats = "mix the flavors" toggle state for treats */
  var state = {
    ball:   { size: '', color: '' },
    ball2:  { size: '', color: '' },
    treat:  { flavor: '' },
    treat2: { flavor: '' },
    mixBalls:  false,
    mixTreats: false,
    busy:      false
  };

  /* Smart default resolver: configured default → exact match → fuzzy contains
     match → first available variant value. Handles the case where Shopify
     variant values include extra text like "(MEDIUM) up to 40 lbs." that
     the configured default ("Medium") doesn't match exactly. */
  function resolveDefault(product, axisName, configuredDefault) {
    var optIdx = findOptionIndex(product, [axisName]);
    if (optIdx < 0) return '';
    var values = getValuesForOption(product, optIdx);
    if (!values.length) return '';

    var configLower = (configuredDefault || '').toLowerCase().trim();

    // 1. Exact match
    if (configLower) {
      var exact = values.find(function (v) { return v.toLowerCase() === configLower; });
      if (exact) return exact;
    }

    // 2. Fuzzy contains (e.g. "Medium" matches "(MEDIUM) up to 40 lbs.")
    if (configLower) {
      var fuzzy = values.find(function (v) { return v.toLowerCase().indexOf(configLower) !== -1; });
      if (fuzzy) return fuzzy;
    }

    // 3. First AVAILABLE value (must have at least one available variant)
    var firstAvail = values.find(function (v) {
      return product.variants.some(function (variant) {
        return variant.options[optIdx] === v && variant.available;
      });
    });
    if (firstAvail) return firstAvail;

    // 4. Last resort: first value (regardless of availability)
    return values[0];
  }

  function initState() {
    state.ball.size   = resolveDefault(DATA.ball,   'size',   DATA.defaults.ball_size);
    state.ball.color  = resolveDefault(DATA.ball,   'color',  DATA.defaults.ball_color);
    state.treat.flavor = resolveDefault(DATA.treats, 'flavor', DATA.defaults.treat_flavor);

    // If the resolved combination still has no available variant (e.g. the
    // resolved color is OOS for the resolved size), nudge to a known-good
    // combination by walking variants for the first ball variant that's
    // available, then matching.
    var ballSizeIdx  = findOptionIndex(DATA.ball, ['size']);
    var ballColorIdx = findOptionIndex(DATA.ball, ['color', 'colour']);
    var ballMatch = findVariant(DATA.ball, ballSizeIdx, state.ball.size, ballColorIdx, state.ball.color);
    if (!ballMatch || !ballMatch.available) {
      var firstAvailBall = DATA.ball.variants.find(function (v) { return v.available; });
      if (firstAvailBall) {
        if (ballSizeIdx >= 0)  state.ball.size  = firstAvailBall.options[ballSizeIdx]  || state.ball.size;
        if (ballColorIdx >= 0) state.ball.color = firstAvailBall.options[ballColorIdx] || state.ball.color;
      }
    }

    // Ball #2 + Treat #2 start as mirrors of #1. Toggling mix does NOT
    // reset them — once the customer differentiates, their choice persists.
    state.ball2.size   = state.ball.size;
    state.ball2.color  = state.ball.color;
    state.treat2.flavor = state.treat.flavor;

    // Treats: same fallback dance — if resolved flavor isn't available,
    // pick the first available treat variant.
    var treatFlavorIdx = findOptionIndex(DATA.treats, ['flavor']);
    var treatMatch = findVariant(DATA.treats, treatFlavorIdx, state.treat.flavor, -1, '');
    if (!treatMatch || !treatMatch.available) {
      var firstAvailTreat = DATA.treats.variants.find(function (v) { return v.available; });
      if (firstAvailTreat) {
        if (treatFlavorIdx >= 0) state.treat.flavor = firstAvailTreat.options[treatFlavorIdx] || state.treat.flavor;
        state.treat2.flavor = state.treat.flavor;
      }
    }
  }

  /* ── Utilities ── */
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  function money(cents) {
    var n = Number(cents || 0) / 100;
    return '$' + n.toFixed(2);
  }

  function findOptionIndex(product, namePattern) {
    for (var i = 0; i < product.options.length; i++) {
      var lower = product.options[i].name.toLowerCase();
      for (var j = 0; j < namePattern.length; j++) {
        if (lower.indexOf(namePattern[j]) !== -1) return i;
      }
    }
    return -1;
  }

  function getValuesForOption(product, optIndex) {
    if (optIndex < 0) return [];
    return product.options[optIndex].values || [];
  }

  function findVariant(product, sizeOptIdx, sizeVal, colorOptIdx, colorVal) {
    for (var i = 0; i < product.variants.length; i++) {
      var v = product.variants[i];
      var matchSize = sizeOptIdx < 0 || v.options[sizeOptIdx] === sizeVal;
      var matchColor = colorOptIdx < 0 || v.options[colorOptIdx] === colorVal;
      if (matchSize && matchColor) return v;
    }
    return null;
  }

  function sortSizes(values) {
    /* Sort by canonical Small → Medium → Large → XL order using substring
       match so values like "(MEDIUM) up to 40 lbs." sort with "Medium". */
    var out = [];
    SIZE_ORDER.forEach(function (s) {
      var sLower = s.toLowerCase();
      var match = values.find(function (v) {
        return v.toLowerCase().indexOf(sLower) !== -1;
      });
      if (match && out.indexOf(match) === -1) out.push(match);
    });
    // Append any value not in canonical order (defensive — covers special editions, custom sizes)
    values.forEach(function (v) {
      if (out.indexOf(v) === -1) out.push(v);
    });
    return out;
  }

  /* ── Compute current selection ── */
  function getCurrentBallVariant() {
    var ballSizeIdx  = findOptionIndex(DATA.ball, ['size']);
    var ballColorIdx = findOptionIndex(DATA.ball, ['color', 'colour']);
    return findVariant(DATA.ball, ballSizeIdx, state.ball.size, ballColorIdx, state.ball.color);
  }

  function getCurrentBall2Variant() {
    // When balls toggle is off, ball2 is by definition a copy of ball1.
    if (!state.mixBalls) return getCurrentBallVariant();
    var ballSizeIdx  = findOptionIndex(DATA.ball, ['size']);
    var ballColorIdx = findOptionIndex(DATA.ball, ['color', 'colour']);
    return findVariant(DATA.ball, ballSizeIdx, state.ball2.size, ballColorIdx, state.ball2.color);
  }

  function getCurrentTreatVariant() {
    var flavorIdx = findOptionIndex(DATA.treats, ['flavor']);
    return findVariant(DATA.treats, flavorIdx, state.treat.flavor, -1, '');
  }

  function getCurrentTreat2Variant() {
    // When treats toggle is off, treat2 is a copy of treat1.
    if (!state.mixTreats) return getCurrentTreatVariant();
    var flavorIdx = findOptionIndex(DATA.treats, ['flavor']);
    return findVariant(DATA.treats, flavorIdx, state.treat2.flavor, -1, '');
  }

  /* ── Pricing ──
     2026-04-27: 2 balls + 2 treats, Mirror Starter Pack pattern.
     ─────────────────────────────────────────────────────────────────
     CRITICAL: this function MUST produce the exact same number Shopify
     produces in the cart. PDP price = cart price is non-negotiable
     (CLAUDE.md "THE BAR"). Any drift here is a brand-grade defect.

     Per-line-item floor — Shopify's actual rounding behavior on
     "Amount off products" percentage discounts:
       ball1_disc  = floor(ball1.price  × rate)
       ball2_disc  = floor(ball2.price  × rate)
       treat1_disc = floor(treat1.price × rate)
       treat2_disc = floor(treat2.price × rate)
       total_disc  = sum of the four
       customer_total = (b1+b2+t1+t2) − total_disc

     Same rule applies whether items coalesce into qty-2 lines or
     stay as separate qty-1 lines: per-unit floor × qty either way.

     Aggregate floor (floor(sum × (1−rate))) drifts by 1–2 cents on
     certain combos — DO NOT switch to that. */
  function calcPricing() {
    var b1 = getCurrentBallVariant();
    var b2 = getCurrentBall2Variant();
    var t1 = getCurrentTreatVariant();
    var t2 = getCurrentTreat2Variant();

    if (!b1 || !b2 || !t1 || !t2) {
      // complete:false means "not all 4 picks resolved to a real variant yet."
      // renderPricing uses this to keep the price hero in its initial
      // "Starting at {floor}" state instead of flipping to "Sold out."
      return { total: 0, was: 0, save: 0, perInstallment: 0, available: false, complete: false };
    }

    var rate = DATA.bundle.discount_rate; // e.g. 0.15 for 15% off

    var ball1Discount  = Math.floor(b1.price * rate);
    var ball2Discount  = Math.floor(b2.price * rate);
    var treat1Discount = Math.floor(t1.price * rate);
    var treat2Discount = Math.floor(t2.price * rate);

    var was   = b1.price + b2.price + t1.price + t2.price;
    var save  = ball1Discount + ball2Discount + treat1Discount + treat2Discount;
    var total = was - save;

    return {
      total: total,
      was: was,
      save: save,
      ball1Discount:  ball1Discount,
      ball2Discount:  ball2Discount,
      treat1Discount: treat1Discount,
      treat2Discount: treat2Discount,
      perInstallment: Math.round(total / 4),
      available: b1.available && b2.available && t1.available && t2.available,
      ballVariant:   b1,
      ball2Variant:  b2,
      treatVariant:  t1,
      treat2Variant: t2,
      qualifiesFreeShip: total >= (DATA.bundle.free_ship_threshold || 4900),
      complete: true
    };
  }

  function renderPricing() {
    var p = calcPricing();
    var nowEl    = $('#pt-price-now');
    var wasEl    = $('#pt-price-was');
    var saveEl   = $('#pt-price-save');
    var labelEl  = $('#pt-price-label');
    var totalEl  = $('#pt-commit-total');
    var commitWasEl = $('#pt-commit-was');
    var savePill = $('#pt-commit-save');
    var shipEl   = $('#pt-commit-ship');
    var shipTxt  = $('#pt-commit-ship-text');
    var stickyP  = $('#pt-sticky-price');
    var atcLabel = $('#pt-atc-label');

    /* Branch 1: customer hasn't completed all 4 picks yet.
       Leave the price hero in its initial "Starting at {floor}" state
       (the Liquid template seeded it from product.price). The commit
       zone and sticky still show "—" because the customer can't ATC
       until everything is picked. This is the Apple/Tesla pattern:
       the floor is honest, the configurator is obvious, no fake $0. */
    if (!p.complete) {
      if (labelEl) {
        labelEl.textContent = 'Starting at';
        labelEl.setAttribute('data-state', 'initial');
      }
      // Don't touch nowEl/wasEl/saveEl — Liquid initialized them to floor.
      if (totalEl) totalEl.textContent = '—';
      if (commitWasEl) commitWasEl.classList.add('hidden');
      if (savePill) savePill.classList.add('hidden');
      if (stickyP) stickyP.textContent = 'Pick your pack';
      if (atcLabel) atcLabel.textContent = 'Pick your pack';
      var atcBtnInit = $('#pt-atc');
      var stkBtnInit = $('#pt-sticky-atc');
      if (atcBtnInit) atcBtnInit.classList.add('is-disabled');
      if (stkBtnInit) stkBtnInit.classList.add('is-disabled');
      return;
    }

    /* Branch 2: complete selection but a chosen variant is out of stock. */
    if (!p.available) {
      if (labelEl) {
        labelEl.textContent = 'Sold out';
        labelEl.setAttribute('data-state', 'unavailable');
      }
      if (nowEl) nowEl.textContent = '—';
      if (wasEl) wasEl.classList.add('hidden');
      if (saveEl) saveEl.classList.add('hidden');
      if (totalEl) totalEl.textContent = '—';
      if (commitWasEl) commitWasEl.classList.add('hidden');
      if (savePill) savePill.classList.add('hidden');
      if (stickyP) stickyP.textContent = 'Sold out';
      if (atcLabel) atcLabel.textContent = 'Out of Stock';
      var atcBtn = $('#pt-atc');
      var stkBtn = $('#pt-sticky-atc');
      if (atcBtn) atcBtn.classList.add('is-disabled');
      if (stkBtn) stkBtn.classList.add('is-disabled');
      return;
    }

    /* Branch 3: complete + available — flip eyebrow to "Your pack"
       and let the existing render logic show the calculated total. */
    if (labelEl) {
      labelEl.textContent = 'Your pack';
      labelEl.setAttribute('data-state', 'configured');
    }

    /* Price hero (top of buy module) */
    if (nowEl) nowEl.textContent = money(p.total);
    if (wasEl) {
      if (p.save > 0) {
        wasEl.textContent = money(p.was);
        wasEl.classList.remove('hidden');
        wasEl.setAttribute('aria-hidden', 'false');
      } else {
        wasEl.classList.add('hidden');
      }
    }
    if (saveEl) {
      if (p.save > 0) {
        saveEl.textContent = 'Save ' + money(p.save);
        saveEl.classList.remove('hidden');
        saveEl.setAttribute('aria-hidden', 'false');
      } else {
        saveEl.classList.add('hidden');
      }
    }

    /* Commit zone (bottom of buy module — locked design 2026-04-27) */
    if (totalEl) totalEl.textContent = money(p.total);
    if (commitWasEl) {
      if (p.save > 0) {
        commitWasEl.textContent = money(p.was);
        commitWasEl.classList.remove('hidden');
      } else {
        commitWasEl.classList.add('hidden');
      }
    }
    if (savePill) {
      if (p.save > 0) {
        savePill.textContent = 'Save ' + money(p.save);
        savePill.classList.remove('hidden');
      } else {
        savePill.classList.add('hidden');
      }
    }

    /* Dynamic shipping line — borrowed from OG WUNDERbundle pattern.
       Two states: under threshold → "Add $X.XX more for free shipping"
                   at/above        → "✓ Free shipping unlocked" (green)
       The data-state attribute drives CSS color/icon swaps. */
    var threshold = (DATA.bundle && DATA.bundle.free_ship_threshold) || 4900;
    if (shipEl && shipTxt) {
      if (p.total >= threshold) {
        shipEl.dataset.state = 'unlocked';
        shipTxt.innerHTML = '<strong>&#x2713; Free shipping unlocked</strong>';
      } else {
        shipEl.dataset.state = 'under';
        var remaining = threshold - p.total;
        shipTxt.innerHTML = 'Add <strong>' + money(remaining) + '</strong> more for free shipping';
      }
    }

    if (stickyP) {
      stickyP.textContent = p.total >= threshold
        ? money(p.total) + ' · Free ship'
        : money(p.total) + ' + $7.95 ship';
    }
    if (atcLabel) atcLabel.textContent = 'Add Starter Pack to Cart — ' + money(p.total);

    var atcBtn = $('#pt-atc');
    var stkBtn = $('#pt-sticky-atc');
    if (atcBtn) atcBtn.classList.remove('is-disabled');
    if (stkBtn) stkBtn.classList.remove('is-disabled');
  }

  /* ── Picker rendering ── */
  /* Parse a Shopify variant value into { label, sub } so the chip can
     show "Small" with "Up to 15 lbs" underneath instead of one verbose
     line. Handles three common patterns:
       "(SMALL) up to 15 lbs."     → { label: "Small", sub: "Up to 15 lbs" }
       "Small / Up to 60 lbs"      → { label: "Small", sub: "Up to 60 lbs" }
       "Small"                      → { label: "Small", sub: "" }
  */
  function parseSizeLabel(value) {
    if (!value) return { label: '', sub: '' };

    // Pattern 1: "(SIZE) descriptor"  e.g. "(SMALL) up to 15 lbs."
    var paren = value.match(/^\(([^)]+)\)\s*(.*)$/);
    if (paren) {
      var primaryParen = paren[1];                        // "SMALL"
      var subParen = (paren[2] || '').replace(/\.$/, ''); // "up to 15 lbs"
      return {
        label: primaryParen.charAt(0).toUpperCase() + primaryParen.slice(1).toLowerCase(),
        sub:   subParen.charAt(0).toUpperCase() + subParen.slice(1)
      };
    }

    // Pattern 2: "Size / weight"  e.g. "Small / Up to 60 lbs"
    var slash = value.match(/^([^\/]+)\/(.+)$/);
    if (slash) {
      return {
        label: slash[1].trim(),
        sub:   slash[2].trim()
      };
    }

    // Pattern 3: "Size: weight"  e.g. "Small: up to 60 lbs"
    var colon = value.match(/^([^:]+):\s*(.+)$/);
    if (colon) {
      return {
        label: colon[1].trim(),
        sub:   (colon[2].trim().charAt(0).toUpperCase() + colon[2].trim().slice(1))
      };
    }

    // Pattern 4: just "Small" — no subtitle
    return { label: value, sub: '' };
  }

  function renderSizeChips(container, values, currentVal, onSelect, available) {
    container.innerHTML = '';
    sortSizes(values).forEach(function (val) {
      var parsed = parseSizeLabel(val);
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'pt-chip';
      chip.setAttribute('role', 'radio');
      chip.setAttribute('aria-checked', val === currentVal ? 'true' : 'false');
      chip.dataset.value = val;
      // Two-line chip: primary label + weight subtitle (matches leash .ww-size-btn pattern)
      var labelHtml = '<span class="pt-chip-label">' + escapeHtml(parsed.label) + '</span>';
      var subHtml = parsed.sub ? '<span class="pt-chip-sub">' + escapeHtml(parsed.sub) + '</span>' : '';
      chip.innerHTML = labelHtml + subHtml;
      if (val === currentVal) chip.classList.add('active');
      if (available && available[val] === false) {
        chip.classList.add('is-unavailable');
        chip.setAttribute('aria-disabled', 'true');
      }
      chip.addEventListener('click', function () { onSelect(val); });
      container.appendChild(chip);
    });
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  /* ════════════════════════════════════════════════════════════════
     CANONICAL SITE-WIDE SWATCH PALETTE
     Mirrored verbatim in:
       • ww-wunderball.css   (--swatch-* CSS variables)
       • ww-pdp.css / ww-main-product.liquid  (inline {%- when -%} cases)
     If a value changes here it MUST change in those files too.
     See CLAUDE.md "Picker Standard" for governance.
     ════════════════════════════════════════════════════════════════ */
  var COLOR_HEX = {
    'swirl':   'conic-gradient(from 45deg, #E8622A, #F0287A, #7B3FF2, #00AAEC, #3BB44A, #E8622A)',
    'orange':  '#E8622A',
    'green':   '#3BB44A',
    'pink':    '#F0287A',
    'purple':  '#7B3FF2',
    'blue':    '#00AAEC',
    'blueberry': '#00AAEC',
    'yellow':  '#F6EC44',
    'red':     '#EC1C24',
    'black':   '#1A1A18',
    'silver':  '#ABABAB',
    'white':   '#FFFFFF',
    'tan':     '#C9A97A',
    'brown':   '#7D5A3C',
    'navy':    '#1F2A4A',
    'gray':    '#8a8a88',
    'grey':    '#8a8a88'
  };

  function colorSwatchStyle(name) {
    var key = (name || '').toLowerCase().trim();
    var val = COLOR_HEX[key];
    if (!val) {
      // fallback: try first match against the start of the name
      for (var k in COLOR_HEX) {
        if (key.indexOf(k) !== -1) { val = COLOR_HEX[k]; break; }
      }
    }
    if (!val) val = '#cccccc';
    return val.indexOf('gradient') !== -1
      ? 'background-image:' + val
      : 'background-color:' + val;
  }

  function renderSwatches(container, values, currentVal, onSelect, available) {
    container.innerHTML = '';
    values.forEach(function (val) {
      var sw = document.createElement('button');
      sw.type = 'button';
      sw.className = 'pt-swatch';
      sw.setAttribute('role', 'radio');
      sw.setAttribute('aria-checked', val === currentVal ? 'true' : 'false');
      sw.setAttribute('aria-label', val);
      sw.dataset.value = val;
      sw.style.cssText = colorSwatchStyle(val);
      if (val === currentVal) sw.classList.add('active');
      if (available && available[val] === false) {
        sw.classList.add('is-unavailable');
        sw.setAttribute('aria-disabled', 'true');
      }
      var dot = document.createElement('span');
      dot.className = 'pt-swatch-dot';
      sw.appendChild(dot);
      sw.addEventListener('click', function () { onSelect(val); });
      container.appendChild(sw);
    });
  }

  /* ── Flavor pill renderer ── (single-axis picker for treats — flavor only)
     WUNDERtreats has a single option ("Dog treat flavor") with two
     long values: "Peanut Butter & Blueberry" and "Sweet Potato & Honey".
     Pills render the full flavor name with an active state + amber
     accent when selected. Wraps to multiple lines on narrow viewports. */
  function renderFlavorPills(container, values, currentVal, onSelect, available) {
    if (!container) return;
    container.innerHTML = '';
    values.forEach(function (val) {
      var pill = document.createElement('button');
      pill.type = 'button';
      pill.className = 'pt-flavor-pill';
      pill.setAttribute('role', 'radio');
      pill.setAttribute('aria-checked', val === currentVal ? 'true' : 'false');
      pill.dataset.value = val;
      pill.textContent = val;
      if (val === currentVal) pill.classList.add('active');
      if (available && available[val] === false) {
        pill.classList.add('is-unavailable');
        pill.setAttribute('aria-disabled', 'true');
      }
      pill.addEventListener('click', function () { onSelect(val); });
      container.appendChild(pill);
    });
  }

  /* ── Availability lookup: which values are in stock for current other selections? ── */
  function buildAvailabilityMap(product, axis, otherAxis, otherVal) {
    var axisIdx  = findOptionIndex(product, [axis]);
    var otherIdx = findOptionIndex(product, [otherAxis]);
    var values = getValuesForOption(product, axisIdx);
    var map = {};
    values.forEach(function (v) {
      var found = product.variants.some(function (variant) {
        var matchAxis = variant.options[axisIdx] === v;
        var matchOther = otherIdx < 0 || variant.options[otherIdx] === otherVal;
        return matchAxis && matchOther && variant.available;
      });
      map[v] = found;
    });
    return map;
  }

  /* ── Wire up pickers ── */
  function rerenderAllPickers() {
    var ballSizes  = getValuesForOption(DATA.ball, findOptionIndex(DATA.ball, ['size']));
    var ballColors = getValuesForOption(DATA.ball, findOptionIndex(DATA.ball, ['color', 'colour']));
    var flavors    = getValuesForOption(DATA.treats, findOptionIndex(DATA.treats, ['flavor']));

    /* Ball #1 pickers (size + color) */
    renderSizeChips(
      $('[data-picker="ball-size"] .pt-picker-chips'),
      ballSizes, state.ball.size,
      function (v) {
        state.ball.size = v;
        // Mirror to ball2 only when balls toggle is OFF
        if (!state.mixBalls) state.ball2.size = v;
        updateAll();
      },
      buildAvailabilityMap(DATA.ball, 'size', 'color', state.ball.color)
    );

    renderSwatches(
      $('[data-picker="ball-color"] .pt-picker-swatches'),
      ballColors, state.ball.color,
      function (v) {
        state.ball.color = v;
        if (!state.mixBalls) state.ball2.color = v;
        updateAll();
      },
      buildAvailabilityMap(DATA.ball, 'color', 'size', state.ball.size)
    );

    /* Ball #2 pickers — same source data, independent target state */
    var ball2SizeContainer = $('[data-picker="ball2-size"] .pt-picker-chips');
    if (ball2SizeContainer) {
      renderSizeChips(
        ball2SizeContainer,
        ballSizes, state.ball2.size,
        function (v) { state.ball2.size = v; updateAll(); },
        buildAvailabilityMap(DATA.ball, 'size', 'color', state.ball2.color)
      );
    }
    var ball2ColorContainer = $('[data-picker="ball2-color"] .pt-picker-swatches');
    if (ball2ColorContainer) {
      renderSwatches(
        ball2ColorContainer,
        ballColors, state.ball2.color,
        function (v) { state.ball2.color = v; updateAll(); },
        buildAvailabilityMap(DATA.ball, 'color', 'size', state.ball2.size)
      );
    }

    /* Treats #1 picker (flavor only, single axis) */
    renderFlavorPills(
      $('[data-picker="treats-flavor"] .pt-flavor-pills'),
      flavors, state.treat.flavor,
      function (v) {
        state.treat.flavor = v;
        // Mirror to treat2 only when treats toggle is OFF
        if (!state.mixTreats) state.treat2.flavor = v;
        updateAll();
      }
    );

    /* Treats #2 picker (only used when treats toggle is on) */
    var treat2Container = $('[data-picker="treats2-flavor"] .pt-flavor-pills');
    if (treat2Container) {
      renderFlavorPills(
        treat2Container,
        flavors, state.treat2.flavor,
        function (v) { state.treat2.flavor = v; updateAll(); }
      );
    }

    /* Selection echoes — single source of truth, written in 2 places:
         1. .pt-picker-label-current (right-aligned next to picker labels)
         2. The commit zone selection lines */
    var ballSizeParsed  = parseSizeLabel(state.ball.size);
    var ball2SizeParsed = parseSizeLabel(state.ball2.size);
    var ballSizeLabel   = ballSizeParsed.label  || state.ball.size;
    var ball2SizeLabel  = ball2SizeParsed.label || state.ball2.size;

    function setCurrent(pickerSelector, axis, value) {
      var grp = $(pickerSelector);
      if (!grp) return;
      var slot = grp.querySelector('[data-current-for="' + axis + '"]');
      if (slot) slot.textContent = value;
    }
    setCurrent('[data-picker="ball-size"]',     'size',   ballSizeLabel);
    setCurrent('[data-picker="ball-color"]',    'color',  state.ball.color);
    setCurrent('[data-picker="ball2-size"]',    'size',   ball2SizeLabel);
    setCurrent('[data-picker="ball2-color"]',   'color',  state.ball2.color);
    setCurrent('[data-picker="treats-flavor"]', 'flavor', state.treat.flavor);
    setCurrent('[data-picker="treats2-flavor"]','flavor', state.treat2.flavor);

    /* Commit zone selection summary — handle 2 split cases independently:
         - balls split: showSplitBalls = mixBalls && variants differ
         - treats split: showSplitTreats = mixTreats && flavors differ
       Resulting lines:
         showSplitBalls=N showSplitTreats=N → "2× ball + 2× treat"
         showSplitBalls=Y showSplitTreats=N → "1× ball#1 + 1× ball#2 + 2× treat"
         showSplitBalls=N showSplitTreats=Y → "2× ball + 1× treat#1 + 1× treat#2"
         showSplitBalls=Y showSplitTreats=Y → "1× ball#1 + 1× ball#2 + 1× treat#1 + 1× treat#2" */
    var ballsIdentical  = (state.ball.size === state.ball2.size) &&
                          (state.ball.color === state.ball2.color);
    var treatsIdentical = (state.treat.flavor === state.treat2.flavor);
    var showSplitBalls  = state.mixBalls  && !ballsIdentical;
    var showSplitTreats = state.mixTreats && !treatsIdentical;

    /* Update the leading qty on the primary ball line ("2×" or "1×") */
    var ballLines = $$('.pt-commit-selection-line');
    var firstBallLine = ballLines[0]; // first selection line (server-rendered "2×")
    if (firstBallLine && firstBallLine.firstChild && firstBallLine.firstChild.nodeType === 3) {
      firstBallLine.firstChild.nodeValue = (showSplitBalls ? '1× ' : '2× ');
    }

    var selBall  = $('#pt-sel-ball');
    var selBall2 = $('#pt-sel-ball2');
    var selBall2Line = $('#pt-sel-ball2-line');
    if (selBall)  selBall.textContent  = ballSizeLabel  + ' ' + state.ball.color  + ' WUNDERball';
    if (selBall2) selBall2.textContent = ball2SizeLabel + ' ' + state.ball2.color + ' WUNDERball';
    if (selBall2Line) {
      if (showSplitBalls) selBall2Line.removeAttribute('hidden');
      else selBall2Line.setAttribute('hidden', '');
    }

    /* Update the leading qty on the primary treat line ("2×" or "1×")
       The treat line is the 3rd .pt-commit-selection-line (after ball, ball2-hidden) */
    var treatPrimaryLine = ballLines.filter(function (el) {
      // Find the line that contains #pt-sel-treat
      return el.querySelector('#pt-sel-treat');
    })[0];
    if (treatPrimaryLine && treatPrimaryLine.firstChild && treatPrimaryLine.firstChild.nodeType === 3) {
      treatPrimaryLine.firstChild.nodeValue = (showSplitTreats ? '1× ' : '2× ');
    }

    var selTreat  = $('#pt-sel-treat');
    var selTreat2 = $('#pt-sel-treat2');
    var selTreat2Line = $('#pt-sel-treat2-line');
    if (selTreat)  selTreat.textContent  = state.treat.flavor  + ' WUNDERtreats';
    if (selTreat2) selTreat2.textContent = state.treat2.flavor + ' WUNDERtreats';
    if (selTreat2Line) {
      if (showSplitTreats) selTreat2Line.removeAttribute('hidden');
      else selTreat2Line.setAttribute('hidden', '');
    }
  }


  function updateAll() {
    rerenderAllPickers();
    renderPricing();
  }

  /* ── Add to Cart ── */
  function setStatus(msg, isError) {
    var el = $('#pt-status');
    if (!el) return;
    el.textContent = msg || '';
    el.className = 'pt-status' + (isError ? ' is-error' : '');
  }

  function addPlayAndTrainToCart(opts) {
    opts = opts || {};
    if (state.busy) return;
    var p = calcPricing();
    if (!p.available) {
      setStatus('That combination is out of stock. Try another size or color.', true);
      return;
    }

    state.busy = true;
    var atcBtn = $('#pt-atc');
    var stkBtn = $('#pt-sticky-atc');
    var atcLabel = $('#pt-atc-label');
    var origLabel = atcLabel ? atcLabel.textContent : 'Add to Cart';
    if (atcLabel) atcLabel.textContent = 'Adding…';
    if (atcBtn) atcBtn.classList.add('is-busy');
    if (stkBtn) stkBtn.classList.add('is-busy');
    setStatus('');

    /* 2026-04-27 — 2-ball + 2-treats bundle, Mirror Starter Pack pattern:
       Cart receives REAL line items only (no $0 shell anchor). Each
       line is tagged with _bundle: 'play-and-train' so the Automatic
       Discount can target. Independent coalescing on both pairs:
         - Same ball variant  → 1 ball line at qty 2
         - Different balls    → 2 separate qty-1 ball lines
         - Same treat flavor  → 1 treat line at qty 2
         - Different flavors  → 2 separate qty-1 treat lines
       Result: cart has 2, 3, or 4 line items depending on combo. */
    var properties = { '_bundle': DATA.bundle.bundle_tag };
    var ballsIdentical  = p.ballVariant.id  === p.ball2Variant.id;
    var treatsIdentical = p.treatVariant.id === p.treat2Variant.id;
    var items = [];
    if (ballsIdentical) {
      items.push({ id: p.ballVariant.id, quantity: 2, properties: properties });
    } else {
      items.push({ id: p.ballVariant.id,  quantity: 1, properties: properties });
      items.push({ id: p.ball2Variant.id, quantity: 1, properties: properties });
    }
    if (treatsIdentical) {
      items.push({ id: p.treatVariant.id, quantity: 2, properties: properties });
    } else {
      items.push({ id: p.treatVariant.id,  quantity: 1, properties: properties });
      items.push({ id: p.treat2Variant.id, quantity: 1, properties: properties });
    }

    /* Path 1: Monster Upsells sequential chain (preferred) */
    var muAdd = window.monster_addToCart || window.monster_atc;
    if (typeof muAdd === 'function') {
      var queue = items.slice();
      function next() {
        if (!queue.length) {
          // All items added.
          // If the caller asked for a redirect (Shop Pay express flow),
          // honor it — items are already in cart, customer goes straight
          // to /checkout where Shop Pay's express handler picks up.
          // Otherwise the MU drawer handles the UX (drawer stays open,
          // customer chooses Checkout vs Continue Shopping).
          state.busy = false;
          if (opts.thenRedirect) {
            if (atcLabel) atcLabel.textContent = 'Going to checkout…';
            window.location.href = opts.thenRedirect;
            return;
          }
          if (atcLabel) atcLabel.textContent = '✓ Added to Cart';
          setTimeout(function () {
            if (atcLabel) atcLabel.textContent = origLabel;
            if (atcBtn) atcBtn.classList.remove('is-busy');
            if (stkBtn) stkBtn.classList.remove('is-busy');
          }, 2400);
          return;
        }
        var item = queue.shift();
        try {
          var ret = muAdd(item);
          if (ret && typeof ret.then === 'function') {
            ret.then(next).catch(function (err) { handleAtcError(err); });
          } else {
            // MU may not return a promise; give it a beat to update DOM
            setTimeout(next, 350);
          }
        } catch (e) {
          handleAtcError(e);
        }
      }
      next();
      return;
    }

    /* Path 2: Fallback /cart/add.js — refresh cart drawer via Section Rendering API */
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: items,
        sections: 'cart-drawer,cart-notification,cart-icon-bubble,cart'
      })
    })
    .then(function (r) {
      if (!r.ok) throw new Error('Cart add failed (' + r.status + ')');
      return r.json();
    })
    .then(function (resp) {
      state.busy = false;
      // Same redirect semantics as the MU path — Shop Pay flow asks for
      // /checkout; standard ATC opens the drawer with the items inside.
      if (opts.thenRedirect) {
        if (atcLabel) atcLabel.textContent = 'Going to checkout…';
        window.location.href = opts.thenRedirect;
        return;
      }
      injectCartSections(resp.sections || {});
      openCartDrawer();
      if (atcLabel) atcLabel.textContent = '✓ Added to Cart';
      setTimeout(function () {
        if (atcLabel) atcLabel.textContent = origLabel;
        if (atcBtn) atcBtn.classList.remove('is-busy');
        if (stkBtn) stkBtn.classList.remove('is-busy');
      }, 2400);
    })
    .catch(handleAtcError);
  }

  function handleAtcError(err) {
    console.error('[PlayAndTrain] ATC error:', err);
    state.busy = false;
    var atcBtn = $('#pt-atc');
    var stkBtn = $('#pt-sticky-atc');
    var atcLabel = $('#pt-atc-label');
    if (atcLabel) atcLabel.textContent = 'Try again';
    if (atcBtn) atcBtn.classList.remove('is-busy');
    if (stkBtn) stkBtn.classList.remove('is-busy');
    setStatus('Something went wrong adding to cart. Please try again or refresh the page.', true);
  }

  function injectCartSections(sections) {
    Object.keys(sections).forEach(function (key) {
      var el = document.querySelector('#shopify-section-' + key) ||
               document.querySelector('[data-section-id="' + key + '"]') ||
               document.querySelector('cart-drawer');
      if (el && sections[key]) el.innerHTML = sections[key];
    });
  }

  function openCartDrawer() {
    var dr = document.querySelector('cart-drawer');
    if (dr && typeof dr.open === 'function') { dr.open(); return; }
    var btn = document.querySelector('[aria-controls="cart-drawer"], [data-cart-drawer-toggle]');
    if (btn && typeof btn.click === 'function') { btn.click(); return; }
  }

  /* ── Gallery (mirror WUNDERball pattern: thumbs toggle .active on slides) ── */
  function activateThumb(targetIdx) {
    $$('.pt-g-thumb').forEach(function (t) {
      var match = String(t.dataset.target) === String(targetIdx);
      t.classList.toggle('active', match);
      t.setAttribute('aria-selected', match ? 'true' : 'false');
    });
    $$('.pt-g-main-slide').forEach(function (s) {
      s.classList.toggle('active', String(s.dataset.slide) === String(targetIdx));
    });
  }

  function bindGallery() {
    $$('.pt-g-thumb').forEach(function (t) {
      t.addEventListener('click', function () { activateThumb(t.dataset.target); });
      t.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activateThumb(t.dataset.target);
        }
      });
    });
  }

  /* ── Sticky bar visibility (matches WUNDERball pattern) ── */
  function bindSticky() {
    var sticky = $('#pt-sticky');
    var commit = $('#pt-commit');
    if (!sticky || !commit) return;
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // Sticky shows when commit zone is OUT of viewport
        sticky.setAttribute('aria-hidden', entry.isIntersecting ? 'true' : 'false');
        sticky.classList.toggle('is-visible', !entry.isIntersecting);
      });
    }, { rootMargin: '0px 0px -100px 0px', threshold: 0 });
    observer.observe(commit);
  }

  /* ── Mix toggles (independent for balls + treats) ──
     Two switches, two state flags, identical UX behavior:
       Toggle ON:  reveal 2nd picker card, hide qty badge, swap title
                   noun from plural ("WUNDERballs") to singular.
       Toggle OFF: hide 2nd picker card, show qty badge, restore plural
                   title, force 2nd state to mirror 1st (so cart adds
                   2× the same variant). */
  function syncMixUXFor(target) {
    /* target is 'balls' or 'treats' */
    var stepId   = (target === 'balls') ? '#pt-step-ball2'  : '#pt-step-treats2';
    var qtyId    = (target === 'balls') ? '#pt-step-balls-qty-badge' : '#pt-step-treats-qty-badge';
    var nounSel  = (target === 'balls')
      ? '.pt-step--ball .pt-step-title-noun'
      : '.pt-step--treats .pt-step-title-noun';
    var stateOn  = (target === 'balls') ? state.mixBalls : state.mixTreats;
    var step     = $(stepId);
    var qtyBadge = $(qtyId);
    var noun     = $(nounSel);
    if (!step) return;
    if (stateOn) {
      step.removeAttribute('hidden');
      step.setAttribute('aria-hidden', 'false');
      if (qtyBadge) qtyBadge.style.display = 'none';
      if (noun) noun.textContent = noun.dataset.nounSingular || noun.dataset.nounPlural || noun.textContent;
    } else {
      step.setAttribute('hidden', '');
      step.setAttribute('aria-hidden', 'true');
      if (qtyBadge) qtyBadge.style.display = '';
      if (noun) noun.textContent = noun.dataset.nounPlural || noun.textContent;
    }
  }

  function bindMixToggles() {
    /* Generic handler — reads data-mix-target on the input to know
       which toggle was flipped. Lets us add more pickers later (e.g.
       Stock-Up Pack with N balls) without rewriting this binding. */
    $$('.pt-mix-toggle-input').forEach(function (input) {
      var target = input.dataset.mixTarget;
      if (!target) return;
      input.addEventListener('change', function () {
        var on = !!input.checked;
        if (target === 'balls') {
          state.mixBalls = on;
          if (!on) {
            // Toggle off — mirror ball2 to ball1 so cart adds 2× same variant.
            state.ball2.size  = state.ball.size;
            state.ball2.color = state.ball.color;
          }
          // Toggle ON — leave ball2 on its current values (mirrors ball1 by
          // default from initState). Don't auto-switch because there are
          // 20 ball variants and "the other one" is ambiguous.
        } else if (target === 'treats') {
          state.mixTreats = on;
          if (on) {
            // Toggle ON — auto-pick the OTHER flavor for bag #2. There are
            // only 2 flavors, so "the other one" is unambiguous and the
            // whole point of toggling "Mix" is to actually have a mix.
            // Saves the customer a click.
            var flavorIdx = findOptionIndex(DATA.treats, ['flavor']);
            var flavors = getValuesForOption(DATA.treats, flavorIdx);
            var other = flavors.find(function (f) { return f !== state.treat.flavor; });
            if (other) state.treat2.flavor = other;
          } else {
            // Toggle off — mirror back to primary for clean qty-2 cart line.
            state.treat2.flavor = state.treat.flavor;
          }
        }
        syncMixUXFor(target);
        updateAll();
      });
    });
    // Sync UX on init in case any toggle is pre-checked
    syncMixUXFor('balls');
    syncMixUXFor('treats');
  }

  /* ── Shop Pay button intercept ──
     The Liquid template renders {{ form | payment_button }} inside a
     <form> for the WUNDER Play & Train Pack product. By default that
     submits the wunderbundle product variant only — useless for our
     multi-item bundle. We intercept the click, run the multi-item ATC
     flow, then route the customer to /checkout where Shop Pay's
     express flow takes over with the real items in cart. */
  function bindShopPayIntercept() {
    var wrap = $('#pt-shoppay-wrap');
    if (!wrap) return;
    /* The dynamic checkout button renders inside the wrap as a Web
       Component or a styled <button>. We bind on the wrap's click —
       capture phase so we run before Shopify's handler dispatches the
       form submit. */
    wrap.addEventListener('click', function (e) {
      if (state.busy) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      var p = calcPricing();
      if (!p.available) {
        e.preventDefault();
        e.stopPropagation();
        setStatus('That combination is out of stock. Try another size or color.', true);
        return;
      }
      // Stop the form's default submit (which would express-checkout the shell variant)
      e.preventDefault();
      e.stopPropagation();
      // Run the multi-item ATC, then route to /checkout where Shop Pay express picks up
      addPlayAndTrainToCart({ thenRedirect: '/checkout' });
    }, true);
  }

  /* ── Init ── */
  function init() {
    initState();           // Resolve defaults against actual variant values + availability
    updateAll();
    bindGallery();
    bindSticky();
    bindMixToggles();
    bindShopPayIntercept();

    var atcBtn = $('#pt-atc');
    var stkBtn = $('#pt-sticky-atc');
    if (atcBtn) atcBtn.addEventListener('click', function () { addPlayAndTrainToCart(); });
    if (stkBtn) stkBtn.addEventListener('click', function () { addPlayAndTrainToCart(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
